import React, { useEffect, useMemo, useState } from "react";
import { Add, DescriptionOutlined, FilterAltOutlined, Refresh, Search } from "@mui/icons-material";
import { Alert, Box, CircularProgress, Pagination, Tooltip, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { CONTRACT_STATUSES } from "constants/projectRules";
import { useAuth } from "context/AuthContext";
import api from "services/api";
import { isAdminUser } from "modules/systems/services/application.service";
import { AppButton } from "components/common/buttons";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { AppSelect, AppTextField, AppYearPicker } from "components/common/inputs";
import SectionCard from "../components/SectionCard";
import SubcontractFormDialog from "../components/SubcontractFormDialog";
import SubcontractListItem from "../components/SubcontractListItem";
import useSubcontractPermissions, { SUBCONTRACT_PERMISSIONS } from "../hooks/useSubcontractPermissions";
import { safely, subcontractApi } from "../services/subcontractApi";

const CURRENT_YEAR = new Date().getFullYear();

const getField = (item, field) =>
  item?.[field] ?? item?.[`${field.charAt(0).toUpperCase()}${field.slice(1)}`];

const getSignYear = (item) => {
  const signDate = getField(item, "signDate");
  if (!signDate) return null;
  const date = new Date(`${String(signDate).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.getFullYear();
};

export default function SubcontractListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = useSubcontractPermissions();
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [yearRange, setYearRange] = useState({ from: CURRENT_YEAR, to: CURRENT_YEAR });
  const [openForm, setOpenForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 6;
  const [allowedSubcontractIds, setAllowedSubcontractIds] = useState(null);
  const [accessRefreshKey, setAccessRefreshKey] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  useEffect(() => {
    const notice = location.state?.snackbar;
    if (!notice?.message) return;
    setSnackbar({ ...notice, open: true });
    const remainingState = { ...location.state };
    delete remainingState.snackbar;
    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: remainingState });
  }, [location, navigate]);

  useEffect(() => {
    let active = true;

    const loadAccessibleSubcontracts = async () => {
      if (!user?.userId) {
        if (active) setAllowedSubcontractIds("none");
        return;
      }

      if (isAdminUser(user)) {
        if (active) setAllowedSubcontractIds("all");
        return;
      }

      try {
        const response = await api.get(`/UserSubcontract/user/${user.userId}`, {
          params: { pageSize: 1000, pageNumber: 1 },
        });
        const entries = response?.data?.data || [];
        const ids = entries
          .map((item) => item.subcontractId ?? item.SubcontractId)
          .filter((value) => value != null);

        if (active) {
          setAllowedSubcontractIds(ids.length > 0 ? ids : "none");
        }
      } catch (requestError) {
        if (active) {
          setAllowedSubcontractIds("error");
          setError(
            requestError.response?.data?.message ||
              requestError.message ||
              "Không thể tải danh sách phân quyền giao khoán.",
          );
        }
      }
    };

    setAllowedSubcontractIds(null);
    loadAccessibleSubcontracts();

    return () => {
      active = false;
    };
  }, [user, accessRefreshKey]);

  const loadData = async () => {
    if (allowedSubcontractIds === null) return;

    setLoading(true);
    setError("");

    if (allowedSubcontractIds === "error" || allowedSubcontractIds === "none") {
      setData([]);
      setLoading(false);
      return;
    }

    try {
      const result = await safely(() =>
        subcontractApi.getSubcontracts({
          pageSize: 1000,
          pageNumber: 1,
          searchTerm: search || undefined,
          status: status || undefined,
          contractIds:
            allowedSubcontractIds !== "all" && Array.isArray(allowedSubcontractIds)
              ? allowedSubcontractIds.join(",")
              : undefined,
        }),
      );
      setData(result.data || []);
    } catch (requestError) {
      setData([]);
      setError(requestError.response?.data?.message || requestError.message || "Không thể tải dữ liệu giao khoán");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [allowedSubcontractIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizedRange = useMemo(() => {
    const from = Number(yearRange.from) || CURRENT_YEAR;
    const to = Number(yearRange.to) || from;
    return {
      from: Math.min(from, to),
      to: Math.max(from, to),
    };
  }, [yearRange]);

  const filtered = useMemo(
    () =>
      data.filter((item) => {
        const keyword = search.toLowerCase();
        const matchesSearch =
          !keyword ||
          [item.subcontractNumber, item.subcontractName, item.assigneeName, item.contractNumber].some((value) =>
            String(value || "").toLowerCase().includes(keyword),
          );
        const matchesStatus = !status || item.status === status;
        const signYear = getSignYear(item);
        const matchesYear = signYear >= normalizedRange.from && signYear <= normalizedRange.to;
        return matchesSearch && matchesStatus && matchesYear;
      }),
    [data, search, status, normalizedRange],
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const displayed = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleYearChange = (field, value) => {
    setYearRange((current) => ({ ...current, [field]: Number(value) || CURRENT_YEAR }));
    setPage(1);
  };

  const handleSubcontractSaved = () => {
    setPage(1);
    if (isAdminUser(user)) {
      return loadData();
    }
    setAccessRefreshKey((current) => current + 1);
  };

  return (
    <Box className="subcontract-page">
      <Box className="subcontract-filter-bar">
        <FilterAltOutlined color="action" />
        <AppTextField
          placeholder="Tìm kiếm ..."
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          sx={{ flex: 1 }}
          startIcon={<Search fontSize="small" />}
        />
        <AppYearPicker
          label="Từ năm"
          value={yearRange.from}
          onChange={(value) => handleYearChange("from", value)}
          sx={{ minWidth: 120, width: 140 }}
        />
        <AppYearPicker
          label="Đến năm"
          value={yearRange.to}
          onChange={(value) => handleYearChange("to", value)}
          sx={{ minWidth: 120, width: 140 }}
        />
        <AppSelect
          label="Trạng thái"
          value={status}
          allowClear
          options={CONTRACT_STATUSES}
          sx={{ minWidth: 120, width: 160 }}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        />
        <AppButton variant="outlined" onClick={loadData}>Áp dụng</AppButton>
        <Tooltip title="Tải lại dữ liệu">
          <AppButton color="inherit" onClick={loadData}><Refresh /></AppButton>
        </Tooltip>
        {permissions.has(SUBCONTRACT_PERMISSIONS.subcontractCreate) && (
          <AppButton variant="contained" startIcon={<Add />} onClick={() => setOpenForm(true)}>
            Thêm quyết định giao khoán
          </AppButton>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <SectionCard title={`${filtered.length} quyết định giao khoán`}>
        {loading ? (
          <Box className="subcontract-list-loading"><CircularProgress size={30} /></Box>
        ) : displayed.length > 0 ? (
          <Box className="subcontract-contract-list">
            {displayed.map((contract) => (
              <SubcontractListItem
                key={contract.subcontractId}
                contract={contract}
                onClick={() => navigate(`/subcontracts/contracts/${contract.subcontractId}`)}
              />
            ))}
            <Box className="subcontract-list-pagination">
              <Typography variant="body2" color="text.secondary">
                Trang {page}/{pageCount}
              </Typography>
              <Pagination page={page} count={pageCount} color="primary" onChange={(_, value) => setPage(value)} />
            </Box>
          </Box>
        ) : (
          <Box className="subcontract-list-empty">
            <DescriptionOutlined />
            <Typography fontWeight={700}>Không tìm thấy quyết định giao khoán</Typography>
            <Typography variant="body2" color="text.secondary">Hãy thử thay đổi từ khóa, năm ký hoặc trạng thái lọc</Typography>
          </Box>
        )}
      </SectionCard>
      <SubcontractFormDialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSaved={handleSubcontractSaved}
      />
      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      />
    </Box>
  );
}
